import React, { useContext, useEffect, useState } from 'react';
import t from 'react-translate';
import { useSelector, useDispatch } from 'react-redux';
import { css } from '@emotion/react';

// redux
import { getAiRolePlay } from 'redux/selectors/timeline';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';

import NvTextArea from 'shared/components/inputs/nv-text-area';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { danger, primary } from 'styles/global_defaults/colors';
import AiConversationContext, { MessageStatus, Sender } from './context';
import useAiRolePlayHelper from '../hooks/use-ai-role-play-helper';
import streamAIResponse from './stream-ai-response';

const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const ChatBar = ({ handleChunk, handleError }) => {
  const { getResponsivePadding } = useAiRolePlayHelper();
  const {
    messages,
    setMessages,
    sessionData,
  } = useContext(AiConversationContext);

  const dispatch = useDispatch();

  const styles = css`
    padding-left: ${getResponsivePadding()}px;
    padding-right: ${getResponsivePadding()}px;
    :focus-within {
      .chat-box {
        border-color: ${primary} !important;
        .send-button {
          color: ${primary} !important;
        }
      }
    }
    .send-message-textarea {
      width: 90%;
      textarea {
        border: none;
        background-color: transparent;
      }
    }
    .send-button {
      width: 10%;
    }
`;
  const { aiRolePlayId } = useContext(AiConversationContext) || {};
  const aiRolePlay = useSelector((state) => getAiRolePlay(state, aiRolePlayId));

  const lastMessage = messages[messages.length - 1];

  useEffect(() => {
    let inActivityTimeout;

    // Start a timeout to show an alert if there's no activity for 5 minutes from learner.
    if ((lastMessage?.status === MessageStatus.SENT || lastMessage?.status === MessageStatus.FAILED)
        && lastMessage.sender === Sender.AI) {
      inActivityTimeout = setTimeout(() => {
        dispatch(addAlertMessage({
          type: AlertMessageType.WARNING,
          header: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SESSION_ENDED(),
          message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.INACTIVE_SESSION(),
          customColor: danger,
        }));
      }, INACTIVITY_TIMEOUT_MS);
    }

    // If the last message is from the learner and is SENT, clear any existing timeout since we have activity.
    if (lastMessage?.status === MessageStatus.SENT && lastMessage.sender === Sender.LEARNER) {
      if (inActivityTimeout) {
        clearTimeout(inActivityTimeout);
      }
    }

    return () => {
      if (inActivityTimeout) {
        clearTimeout(inActivityTimeout);
      }
    };
  }, [lastMessage, dispatch]);

  const [message, setMessage] = useState('');

  const isSentMessageDisabled = !message.trim()
    || lastMessage?.status === MessageStatus.PENDING || lastMessage?.sender === Sender.LEARNER;

  const handleSendMessage = () => {
    setMessages(prevMessages => [
      ...prevMessages,
      {
        content: message,
        sender: Sender.LEARNER,
        createdAt: new Date(),
        status: MessageStatus.SENT,
      },
    ]);
    streamAIResponse({
      token: sessionData.jwtToken || '',
      userMessage: message,
      onChunk: handleChunk,
      onError: handleError,
    });
    setMessage('');
  };

  return (
    <div css={styles} className='w-100 align-items-center mt-auto py-4'>
      <div className='d-flex border-bottom pb-4 bg-white chat-box'>
        <NvTextArea
          className='send-message-textarea'
          placeholder={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SEND_MESSAGE_PLACEHOLDER()}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <ClickableContainer
          onClick={handleSendMessage}
          className='send-button text-gray-4'
          aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SEND_MESSAGE()}
          disabled={isSentMessageDisabled}
        >
          <NvIcon
            icon='admin-image-right'
            size='small'
            className='d-flex align-items-center mr-4 ml-auto'
          />
        </ClickableContainer>
      </div>
    </div>
  );
};

export default ChatBar;
